import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw9119Api, F919 } from '../../../services/pw9119Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './usePW9119Mode';

export function usePW9119Record() {
  const [record, setRecord] = useState<F919 | null>(null);
  const [formData, setFormData] = useState<Partial<F919>>({});
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by card number */
  const loadRecord = useCallback(async (cardNo: string): Promise<F919 | null> => {
    setLoading(true);
    try {
      const data = await pw9119Api.get(cardNo);
      setRecord(data);
      setFormData({ ...data });
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback(<K extends keyof F919>(key: K, value: F919[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback(<K extends keyof F919>(key: K): F919[K] | string => {
    return (formData as Partial<F919>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F919) => void,
  ) => {
    setLoading(true);
    try {
      const req = { master: formData as F919 };
      if (mode === 'add') {
        const result = await pw9119Api.create(req);
        message.success(`新增成功: ${result.f919_01}`);
        onSuccess(result);
      } else {
        const result = await pw9119Api.update(formData.f919_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pw9119Api.delete(record.f919_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      f919_01: '',
      f919_11: '',
      f919_12: null,
      f919_13: null,
      f919_14: '',
      f919_15: '',
      f919_91: '',
      f919_92: null,
      f919_93: '',
    });
  }, [clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F919) => {
    setRecord(result);
    setFormData({ ...result });
  }, []);

  return {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
